import { useMemo } from 'react';

import type { CalculatorDto } from '@/lib/api/tools';

import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import type {
  RawOption,
  SelectionMap,
  SelectionValue
} from '../hooks/useCostCalculatorState';
import {
  normalizeOption,
  sectionAnchorFromLegacy
} from '../hooks/useCostCalculatorState';

export type CalculatorSectionQuestionView = {
  legacyId: string;
  label: string;
  options: { value: string; label: string }[];
  selectedOptions: string | string[];
  isMulti: boolean;
  showInsuranceWarning: boolean;
};

export type CalculatorSectionView = {
  legacyId: string;
  title?: string | null;
  anchor?: string;
  isExpanded: boolean;
  collapsedLabel: string | null;
  defaultLabel: string;
  otherTotals: Array<{ legacyId: string; label: string; amount: number }>;
  questions: CalculatorSectionQuestionView[];
  showInsuranceAside: boolean;
  ad: CalculatorDto['sections'][number]['ad'] | null | undefined;
};

export type UseCalculatorSectionsArgs = {
  sections: CalculatorDto['sections'];
  outputs: CalculatorDto['outputs'];
  selections: SelectionMap;
  expandedSections: Record<string, boolean>;
  sectionTotals: Record<string, Record<string, number>>;
  breed?: CostCalculatorBreed | null;
  defaultOutput?: { legacyId: string } | null;
};

const isInsuranceQuestion = (
  question: CalculatorDto['sections'][number]['questions'][number]
) =>
  (question.questionKey ?? question.legacyId ?? '').toLowerCase() ===
  'insurance';

const isInsuranceAnswered = (selection: SelectionValue | undefined) => {
  if (Array.isArray(selection)) return selection.length > 0;
  return Boolean(selection);
};

const toChipOptions = (
  question: CalculatorDto['sections'][number]['questions'][number]
) =>
  question.options
    .map((option: RawOption, idx: number) => {
      const normalized = normalizeOption(option, `${question.legacyId}-${idx}`);
      if (!normalized) return null;
      return { value: normalized.value, label: normalized.title };
    })
    .filter((item): item is { value: string; label: string } => Boolean(item));

const toSelectedForChipField = (
  selection: SelectionValue | undefined,
  isMulti: boolean
) => {
  if (isMulti) {
    if (Array.isArray(selection)) return selection;
    if (selection) return [selection];
    return [];
  }
  if (Array.isArray(selection)) return selection[0] ?? '';
  return selection ?? '';
};

export function useCalculatorSections({
  sections,
  outputs,
  selections,
  expandedSections,
  sectionTotals,
  breed,
  defaultOutput
}: UseCalculatorSectionsArgs) {
  const sectionViews = useMemo<CalculatorSectionView[]>(() => {
    return sections.map((section) => {
      const anchor = sectionAnchorFromLegacy(section.legacyId, section.title);
      const isExpanded = expandedSections[section.legacyId] ?? false;
      const sectionTotal = sectionTotals[section.legacyId] ?? {};

      const defaultLabel = defaultOutput
        ? `$${(sectionTotal[defaultOutput.legacyId] ?? 0).toFixed(2)}`
        : '';

      const otherTotals = defaultOutput
        ? outputs
            .filter((out) => out.legacyId !== defaultOutput.legacyId)
            .map((out) => ({
              legacyId: out.legacyId,
              label: out.label ?? out.name,
              amount: sectionTotal[out.legacyId] ?? 0
            }))
        : [];

      const collapsedLabel = defaultOutput ? defaultLabel : null;

      const insuranceQuestion = section.questions.find((q) =>
        isInsuranceQuestion(q)
      );
      const insuranceSelection = insuranceQuestion
        ? selections[insuranceQuestion.legacyId]
        : undefined;
      const insuranceAnswered = isInsuranceAnswered(insuranceSelection);

      const questions: CalculatorSectionQuestionView[] = section.questions.map(
        (question) => {
          const isMulti = Boolean(question.multiSelect);
          const selection = selections[question.legacyId];

          return {
            legacyId: question.legacyId,
            label: question.label ?? question.questionKey ?? 'Question',
            options: toChipOptions(question),
            selectedOptions: toSelectedForChipField(selection, isMulti),
            isMulti,
            showInsuranceWarning:
              isInsuranceQuestion(question) &&
              insuranceAnswered &&
              Boolean(breed)
          };
        }
      );

      const showInsuranceAside =
        section.legacyId === 'cost-of-care' &&
        insuranceAnswered &&
        Boolean(breed);

      return {
        legacyId: section.legacyId,
        title: section.title,
        anchor,
        isExpanded,
        collapsedLabel,
        defaultLabel,
        otherTotals,
        questions,
        showInsuranceAside,
        ad: section.ad ?? null
      };
    });
  }, [
    sections,
    expandedSections,
    sectionTotals,
    defaultOutput,
    outputs,
    selections,
    breed
  ]);

  return { sectionViews };
}
